import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuStar } from "react-icons/lu";
import type { EmoteKind } from "../../shared/avatar";
import { useApp } from "../store";
import { Avatar } from "./Avatar";
import { Confetti } from "./fx";
import { PodiumStage, useDialog } from "./heroes";
import { useOnChange } from "./juice";

/**
 * v17: "Niveau op!" – a full-screen celebration with the player's own figure on the podium (3D where the computer can, a flat picture
 * otherwise). It appears a couple of seconds after the level went up, so the game's result screen (coins flying to the counter) is seen first.
 * It closes by itself after a while, with a click, or with Escape. The sound is played by the result screen (the "levelup" cue).
 */
export function LevelUpWatcher() {
  const { me } = useApp();
  const [level, setLevel] = useState<number | null>(null);
  useOnChange(me?.level ?? 0, (now, before) => {
    if (me?.role === "student" && before > 0 && now > before) setTimeout(() => setLevel(now), 2600);
  }, me?.id);
  if (level === null || !me) return null;
  return <LevelUp level={level} onClose={() => setLevel(null)} />;
}

function LevelUp({ level, onClose }: { level: number; onClose: () => void }) {
  const { me } = useApp();
  const ref = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(0);
  useDialog(ref, onClose);
  useEffect(() => {
    const a = setTimeout(() => setN(1), 500);
    const b = setTimeout(onClose, 9000);
    return () => { clearTimeout(a); clearTimeout(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const e = me!.avatar.emote?.data as { kind?: EmoteKind; victory?: boolean } | undefined;
  const emote: EmoteKind = e?.victory && e.kind ? e.kind : "cheer";
  const left = Math.max(0, me!.levelProgress.needed - me!.levelProgress.into);
  return createPortal(
    <div className="unlock levelup" role="dialog" aria-modal="true" aria-labelledby="lv-h" ref={ref} tabIndex={-1} style={{ ["--t1" as string]: "#ffc61a", ["--t2" as string]: "#5b3bd6" }} onClick={onClose}>
      <div className="ul-rays" aria-hidden="true" />
      <Confetti n={80} ms={5200} />
      <div className="ul-card">
        <span className="ul-kicker"><LuStar aria-hidden="true" size={18} /> Niveau op!</span>
        <div className="ul-stage">
          <PodiumStage equipped={me!.avatar} progress={1} celebrate={{ kind: emote, n }} label="Din figur fejrer det nye niveau" fallback={<Avatar equipped={me!.avatar} size={190} framing="full" label="Din figur" />} />
        </div>
        <h2 id="lv-h" className="title-game ul-name">Niveau {level}!</h2>
        <p className="ul-epi">{me!.title ?? "Flot klaret"}</p>
        <p className="ul-lore">{left > 0 ? `Du er nået op på niveau ${level}. Der er kun ${left} XP til niveau ${level + 1} – bliv ved!` : `Du er nået op på niveau ${level}. Bliv ved – næste niveau venter!`}</p>
        <div className="ul-actions"><button type="button" className="btn lime" onClick={onClose}>Fedt!</button></div>
      </div>
    </div>,
    document.body,
  );
}
